export const USER_ROLES = Object.freeze({
  ADMIN: 'admin',
  UPDATER: 'updater',
  ACCOUNTANT: 'accountant',
  EDITOR: 'editor',
});

const LEGACY_ROLE_ALIASES = Object.freeze({
  updator: USER_ROLES.UPDATER,
});

export const ACCESS_KEYS = Object.freeze({
  DASHBOARD: 'dashboard',
  CONTENT: 'content',
  USERS: 'users',
  USER_DETAILS: 'userDetails',
  TINGS: 'tings',
  TING_DETAILS: 'tingDetails',
  WHATSAPP: 'whatsapp',
  SETTINGS: 'settings',
  EMAIL_SCHEDULER: 'emailScheduler',
  POST_TESTS: 'postTests',
  POST_TEST_DETAILS: 'postTestDetails',
  NEW_POST_TEST: 'newPostTest',
});

export const SETTINGS_SECTION_KEYS = Object.freeze({
  BANK_EXPORT: 'bankExport',
  SIGHTINGS_EXPORT: 'sightingsExport',
  ADMIN_TOOLS: 'adminTools',
  ROLE_ASSIGNMENT: 'roleAssignment',
});

export const ROLE_OPTIONS = [
  { value: USER_ROLES.ADMIN, label: 'Admin' },
  { value: USER_ROLES.UPDATER, label: 'Updater' },
  { value: USER_ROLES.ACCOUNTANT, label: 'Accountant' },
  { value: USER_ROLES.EDITOR, label: 'Editor' },
];

export const USER_ROLE_OPTIONS = [
  { value: 'user', label: 'User' },
  ...ROLE_OPTIONS,
  { value: 'guide', label: 'Guide' },
  { value: 'contributor', label: 'Contributor' },
];

const ROLE_VALUES = ROLE_OPTIONS.map((option) => option.value);
const USER_ROLE_VALUES = USER_ROLE_OPTIONS.map((option) => option.value);

const canonicalizeRole = (role) => LEGACY_ROLE_ALIASES[role] || role;

const routeAccessMap = {
  [ACCESS_KEYS.DASHBOARD]: [USER_ROLES.ADMIN],
  [ACCESS_KEYS.CONTENT]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER, USER_ROLES.EDITOR],
  [ACCESS_KEYS.USERS]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER],
  [ACCESS_KEYS.USER_DETAILS]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER],
  [ACCESS_KEYS.TINGS]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER, USER_ROLES.EDITOR],
  [ACCESS_KEYS.TING_DETAILS]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER, USER_ROLES.EDITOR],
  [ACCESS_KEYS.WHATSAPP]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER],
  [ACCESS_KEYS.SETTINGS]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER, USER_ROLES.ACCOUNTANT],
  [ACCESS_KEYS.EMAIL_SCHEDULER]: [USER_ROLES.ADMIN],
  [ACCESS_KEYS.POST_TESTS]: [USER_ROLES.ADMIN, USER_ROLES.EDITOR],
  [ACCESS_KEYS.POST_TEST_DETAILS]: [USER_ROLES.ADMIN, USER_ROLES.EDITOR],
  [ACCESS_KEYS.NEW_POST_TEST]: [USER_ROLES.ADMIN, USER_ROLES.EDITOR],
};

const settingsAccessMap = {
  [SETTINGS_SECTION_KEYS.BANK_EXPORT]: [USER_ROLES.ADMIN, USER_ROLES.ACCOUNTANT],
  [SETTINGS_SECTION_KEYS.SIGHTINGS_EXPORT]: [USER_ROLES.ADMIN, USER_ROLES.UPDATER],
  [SETTINGS_SECTION_KEYS.ADMIN_TOOLS]: [USER_ROLES.ADMIN],
  [SETTINGS_SECTION_KEYS.ROLE_ASSIGNMENT]: [USER_ROLES.ADMIN],
};

const defaultPathByRole = {
  [USER_ROLES.ADMIN]: '/',
  [USER_ROLES.UPDATER]: '/content',
  [USER_ROLES.ACCOUNTANT]: '/settings',
  [USER_ROLES.EDITOR]: '/content',
};

export const NAV_ITEMS = [
  { label: 'Dashboard', path: '/', event: 'dashboard', accessKey: ACCESS_KEYS.DASHBOARD },
  { label: 'Content', path: '/content', event: 'content', accessKey: ACCESS_KEYS.CONTENT },
  { label: 'Users', path: '/users', event: 'users', accessKey: ACCESS_KEYS.USERS },
  { label: 'Tings', path: '/tings', event: 'tings', accessKey: ACCESS_KEYS.TINGS },
  { label: 'Post Tests', path: '/posttests', event: 'posttests', accessKey: ACCESS_KEYS.POST_TESTS },
  { label: 'WhatsApp', path: '/whatsapp-parks', event: 'whatsapp-parks', accessKey: ACCESS_KEYS.WHATSAPP },
  { label: 'Email Scheduler', path: '/email-scheduler', event: 'email-scheduler', accessKey: ACCESS_KEYS.EMAIL_SCHEDULER },
  { label: 'Settings', path: '/settings', event: 'settings', accessKey: ACCESS_KEYS.SETTINGS },
];

export const normalizeRole = (role) => {
  if (typeof role !== 'string') {
    return null;
  }

  const normalizedRole = canonicalizeRole(role.trim().toLowerCase());
  return ROLE_VALUES.includes(normalizedRole) ? normalizedRole : null;
};

export const isRecognizedRole = (role) => Boolean(normalizeRole(role));

export const getRoleLabel = (role) =>
  USER_ROLE_OPTIONS.find((option) => option.value === canonicalizeRole(String(role || '').trim().toLowerCase()))?.label || 'Unknown';

export const getRoleFromUserDoc = (userDoc = {}) => normalizeRole(userDoc.userRole);

export const getStoredUserRoleFromUserDoc = (userDoc = {}) => {
  if (typeof userDoc.userRole !== 'string') {
    return null;
  }

  const normalizedRole = canonicalizeRole(userDoc.userRole.trim().toLowerCase());
  return USER_ROLE_VALUES.includes(normalizedRole) ? normalizedRole : null;
};

export const hasRouteAccess = (role, accessKey) => {
  const normalizedRole = normalizeRole(role);
  return Boolean(normalizedRole && routeAccessMap[accessKey]?.includes(normalizedRole));
};

export const hasSettingsAccess = (role, sectionKey) => {
  const normalizedRole = normalizeRole(role);
  return Boolean(normalizedRole && settingsAccessMap[sectionKey]?.includes(normalizedRole));
};

export const getDefaultPathForRole = (role) =>
  defaultPathByRole[normalizeRole(role)] || '/login';

export const getNavItemsForRole = (role) =>
  NAV_ITEMS.filter((item) => hasRouteAccess(role, item.accessKey));
